import { z } from "zod";
import { leadDepositSchema } from "./deposit.js";
import { e164Schema, emailSchema, leadRefSchema, localeSchema, timestampSchema, } from "../common.js";
/** System-managed intake status (section 8). Never edited by hand. */
export const INTAKE_STATUSES = [
    "in_progress",
    "completed",
    "whatsapp_opened",
    "booking_started",
    "booked",
    "abandoned",
];
export const intakeStatusSchema = z.enum(INTAKE_STATUSES);
export const answerValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.record(z.string(), z.boolean()),
]);
export const leadAnswerSchema = z.object({
    value: answerValueSchema,
    label: z.string().max(200).optional(),
    answeredAt: timestampSchema,
});
export const leadContactSchema = z.object({
    fullName: z.string().max(200).optional(),
    email: emailSchema.optional(),
    phone: e164Schema.optional(),
    dateOfBirth: z.string().optional(),
    gender: z.string().max(40).optional(),
    location: z.string().max(200).optional(),
});
export const leadSourceSchema = z.object({
    pageUrl: z.string().max(2048).optional(),
    pagePath: z.string().max(1024).optional(),
    referrer: z.string().max(2048).optional(),
    utm: z.record(z.string(), z.string()).optional(),
    device: z.enum(["mobile", "tablet", "desktop"]).optional(),
    language: z.string().max(16).optional(),
    visitId: z.string().optional(),
    /** A contact form on the clinic's site (owner request, 15 October 2026). */
    form: z
        .object({ plugin: z.string().max(40), id: z.string().max(64), title: z.string().max(120) })
        .optional(),
});
/**
 * Where a lead came in (owner request, 15 October 2026): the chat (WhatsApp funnel), a contact
 * form on the clinic's site, or the booking page. Older leads have no channel; see `leadChannel`.
 */
export const LEAD_CHANNELS = ["chat", "form", "booking"];
export const leadSchema = z.object({
    ref: leadRefSchema,
    intakeStatus: intakeStatusSchema,
    channel: z.enum(LEAD_CHANNELS).optional(),
    formId: z.string(),
    formVersionId: z.string(),
    contact: leadContactSchema.default({}),
    answers: z.record(z.string(), leadAnswerSchema).default({}),
    consent: z.object({
        items: z.record(z.string(), z.boolean()),
        textVersion: z.string(),
        acceptedAt: timestampSchema,
        locale: localeSchema,
    }),
    source: leadSourceSchema.default({}),
    progress: z.object({
        lastNodeId: z.string().optional(),
        completedSteps: z.array(z.string()).default([]),
        completed: z.boolean().default(false),
    }),
    whatsappOpenedAt: timestampSchema.optional(),
    /** GoHighLevel contact id once the lead was pushed there. */
    ghlContactId: z.string().max(64).optional(),
    ghlSyncedAt: timestampSchema.optional(),
    /** "Send by email instead" was used: the enquiry went to the clinic by email (D20 exception). */
    emailSentAt: timestampSchema.optional(),
    /** Older field from the mailto version (14 October 2026, a few hours); kept for those leads. */
    emailOpenedAt: timestampSchema.optional(),
    /** Possible duplicates by phone or email in the same clinic; a hint, never a merge (section 8). */
    duplicateOf: z
        .array(z.object({ leadId: z.string(), ref: z.string() }))
        .max(5)
        .optional(),
    /** Opened WhatsApp and wants it soon or finished the funnel. */
    hot: z.boolean().optional(),
    hotReason: z.string().max(60).optional(),
    renderedMessage: z.string().optional(),
    /** The patient's note from the booking page; printed in their WhatsApp message and email. */
    patientNote: z.string().max(800).optional(),
    // PA-managed (10.2)
    stage: z.string(),
    stageChangedAt: timestampSchema,
    stageChangedBy: z.string().optional(),
    /** Latest live appointment, denormalised for lists and the WhatsApp message (section 11). */
    appointment: z
        .object({
        id: z.string(),
        startAt: timestampSchema,
        typeName: z.string().max(80),
        doctorName: z.string().max(120),
        mode: z.enum(["online", "in_person"]),
        status: z.enum(["held", "confirmed", "cancelled", "completed", "no_show"]),
        locationName: z.string().max(80).optional(),
        locationId: z.string().optional(),
        procedures: z.array(z.string().max(120)).max(20).optional(),
    })
        .optional(),
    /** Latest deposit request (17 October 2026). */
    deposit: leadDepositSchema.optional(),
    followUpAt: timestampSchema.optional(),
    /** Set by the follow-up sweep when the due push went out; cleared whenever the date changes. */
    followUpNotifiedAt: timestampSchema.optional(),
    assignedTo: z.string().optional(),
    doctorId: z.string().optional(),
    tags: z.array(z.string().max(40)).max(30).default([]),
    internalNotes: z.string().max(5000).default(""),
    /** Denormalised from the notes log for the list view (10.2). */
    lastNote: z.string().max(120).optional(),
    lastNoteAt: timestampSchema.optional(),
    noteCount: z.number().int().min(0).optional(),
    closeReason: z.string().max(200).optional(),
    firstActionAt: timestampSchema.optional(),
    fileCount: z.number().int().min(0).default(0),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastActivityAt: timestampSchema,
});
/** Fields a PA may change from the dashboard (everything else is server-owned). */
export const LEAD_PA_EDITABLE_FIELDS = [
    "stage",
    "stageChangedAt",
    "stageChangedBy",
    "followUpAt",
    "followUpNotifiedAt",
    "appointment",
    "assignedTo",
    "doctorId",
    "tags",
    "internalNotes",
    "lastNote",
    "lastNoteAt",
    "noteCount",
    "closeReason",
    "firstActionAt",
    "updatedAt",
];
/** Dated notes PAs leave on a lead for follow-up (10 screen 3). Internal only; never sent anywhere. */
export const leadNoteSchema = z.object({
    text: z.string().min(1).max(2000),
    byUid: z.string(),
    byName: z.string().max(120).optional(),
    impersonatedBy: z.string().optional(),
    at: timestampSchema,
    editedAt: timestampSchema.optional(),
});
export const leadEventSchema = z.object({
    type: z.string().min(1).max(60),
    at: timestampSchema,
    actorUid: z.string().optional(),
    impersonatedBy: z.string().optional(),
    data: z.record(z.string(), z.unknown()).default({}),
});
export const leadFileSchema = z.object({
    nodeId: z.string(),
    storagePath: z.string().max(512),
    contentType: z.string().max(100),
    size: z.number().int().min(0),
    originalName: z.string().max(255).optional(),
    thumbnailPath: z.string().max(512).optional(),
    verified: z.boolean().default(false),
    uploadedAt: timestampSchema,
});
export const tagSchema = z.object({
    label: z.string().min(1).max(40),
    colour: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    order: z.number().int().default(0),
});
/** The lead's channel, inferring older leads: the booking page wrote formVersionId "booking" or consent "booking-page-1". */
export function leadChannel(lead) {
    if (lead.channel)
        return lead.channel;
    if (lead.formVersionId === "booking" || lead.consent?.textVersion === "booking-page-1")
        return "booking";
    return "chat";
}
//# sourceMappingURL=lead.js.map